import { ArrowRightIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import clsxm from '@/lib/clsxm';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

import { findActiveByPath, toolCategories } from '@/constants/toolCategories';
import { useUser } from '@/utils/useUser';

import { ALWAYS_SHOWN_SLUGS } from './nav/navModel';
import {
  nav,
  NavItemBadge,
  type NavVariant,
  PinButton,
  useNavTrack,
} from './nav/navUi';

/** Header colours per category, as on the dashboard's and /presets' app icons. */
const CATEGORY_TINT: Record<string, string> = {
  agents:
    'bg-primary-100 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300',
  images:
    'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
  videos: 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
  audio: 'bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-300',
};

const OPEN_DELAY = 90;
const CLOSE_DELAY = 220;

/**
 * Open state of the Tools flyout: hover with a short delay on both sides (so
 * the pointer can travel from the row to the panel), or a click or keyboard
 * press on the row's chevron, which also moves focus into the panel.
 */
export function useToolsFlyout() {
  const router = useRouter();
  const [state, setState] = useState<{
    anchor: HTMLElement;
    focus: boolean;
  } | null>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);

  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };
  const close = useCallback(() => {
    clearTimers();
    setState(null);
  }, []);

  useEffect(() => {
    router.events.on('routeChangeStart', close);
    return () => {
      router.events.off('routeChangeStart', close);
      clearTimers();
    };
  }, [router.events, close]);

  return {
    open: !!state,
    anchor: state?.anchor ?? null,
    focusOnOpen: !!state?.focus,
    close,
    /** Pointer entered the trigger. */
    hoverIn(anchor: HTMLElement) {
      window.clearTimeout(closeTimer.current);
      if (state) return;
      window.clearTimeout(openTimer.current);
      openTimer.current = window.setTimeout(
        () => setState({ anchor, focus: false }),
        OPEN_DELAY,
      );
    },
    /** Pointer left the trigger or the panel. */
    hoverOut() {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => setState(null), CLOSE_DELAY);
    },
    /** Pointer is over the panel. */
    keep() {
      window.clearTimeout(closeTimer.current);
    },
    toggle(anchor: HTMLElement) {
      clearTimers();
      setState((current) => (current ? null : { anchor, focus: true }));
    },
  };
}

interface ToolsFlyoutProps {
  /** The row (sidebar) or icon (rail) it opens from. */
  anchor: HTMLElement;
  id: string;
  variant: NavVariant;
  focusOnOpen: boolean;
  onClose: (returnFocus: boolean) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Every app by category, next to the sidebar's Tools row: the same taxonomy
 * as each tool page's ToolsNav (`toolCategories`). Stars pin a tool to the
 * sidebar. Text tools live in the /presets catalogue, linked at the bottom.
 * Portalled with fixed coordinates so the sidebar's scroll box can't clip it.
 */
export default function ToolsFlyout({
  anchor,
  id,
  variant,
  focusOnOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: ToolsFlyoutProps) {
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const track = useNavTrack(variant);
  const active = findActiveByPath(router.asPath);
  const { favoriteTools, toggleFavorite } = useFavoriteTools();
  // Pinning needs an account: visitors' pins only bounced off a 401.
  const { user } = useUser();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    side: number;
    width: number;
    rtl: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const bar =
        anchor
          .closest<HTMLElement>('[data-nav-panel]')
          ?.getBoundingClientRect() ?? rect;
      const rtl = document.documentElement.dir === 'rtl';
      const gap = 8;
      const room = rtl
        ? bar.left - gap * 2
        : window.innerWidth - bar.right - gap * 2;
      const width = Math.max(320, Math.min(840, room));
      const height = panelRef.current?.offsetHeight ?? 0;
      const top = Math.max(
        gap,
        Math.min(rect.top - 12, window.innerHeight - height - gap),
      );
      const side = rtl ? window.innerWidth - bar.left + gap : bar.right + gap;
      setPosition({ top, side, width, rtl });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  // A second pass once the panel has its height, so it never runs off the bottom.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !position) return;
    const rect = anchor.getBoundingClientRect();
    const top = Math.max(
      8,
      Math.min(rect.top - 12, window.innerHeight - panel.offsetHeight - 8),
    );
    if (top !== position.top) setPosition({ ...position, top });
  }, [anchor, position]);

  useEffect(() => {
    if (focusOnOpen && position) {
      panelRef.current?.querySelector<HTMLElement>('a[href]')?.focus();
    }
  }, [focusOnOpen, position]);

  // Clicks and focus outside the panel and its trigger close it.
  useEffect(() => {
    const outside = (target: EventTarget | null) =>
      target instanceof Node &&
      !panelRef.current?.contains(target) &&
      !anchor.contains(target);
    const onPointer = (event: PointerEvent) => {
      if (outside(event.target)) onClose(false);
    };
    const onFocus = (event: FocusEvent) => {
      if (outside(event.target)) onClose(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('focusin', onFocus);
    };
  }, [anchor, onClose]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose(true);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role='region'
      aria-label={t('Tools')}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      style={
        position
          ? {
              top: position.top,
              width: position.width,
              [position.rtl ? 'right' : 'left']: position.side,
            }
          : { visibility: 'hidden', top: 0, left: 0 }
      }
      className={clsxm(
        'fixed z-50 overflow-hidden rounded-2xl motion-safe:animate-rise-in',
        'bg-white ring-1 ring-[#0f0f28]/[0.08] shadow-[0_1px_2px_rgba(16,16,40,0.06),0_28px_60px_-24px_rgba(49,46,129,0.35)]',
        'dark:bg-slate-800 dark:ring-white/10 dark:shadow-[0_28px_60px_-24px_rgba(0,0,0,0.7)]',
      )}
    >
      <div className='grid grid-cols-4 gap-x-2 p-3'>
        {toolCategories.map((category) => (
          <div key={category.slug} className='min-w-0'>
            <div className='flex items-center gap-2 px-2 pb-1.5 pt-1'>
              <span
                aria-hidden='true'
                className={clsxm(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                  CATEGORY_TINT[category.slug] ?? nav.soft,
                )}
              >
                <category.icon className='h-3.5 w-3.5' />
              </span>
              <h3
                className={clsxm('truncate text-[13px] font-semibold', nav.ink)}
              >
                {t(category.title)}
              </h3>
            </div>
            <ul>
              {category.tools.map((tool) => {
                const isActive = active?.tool.slug === tool.slug;
                const pinned = favoriteTools.includes(tool.slug);
                return (
                  <li
                    key={`${category.slug}-${tool.slug}`}
                    className={clsxm(
                      'group/row flex h-8 items-center gap-1 rounded-lg pe-1 transition-colors',
                      isActive
                        ? nav.active
                        : 'hover:bg-[#f4f4fb] dark:hover:bg-white/[0.06]',
                    )}
                  >
                    <Link
                      href={tool.href}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() =>
                        track('flyout', tool.href, { category: category.slug })
                      }
                      className={clsxm(
                        'flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-lg ps-2 text-[13px]',
                        isActive
                          ? 'font-medium'
                          : clsxm(
                              nav.body,
                              'group-hover/row:text-[#10101c] dark:group-hover/row:text-white',
                            ),
                        nav.focus,
                      )}
                    >
                      <span className='truncate'>{t(tool.name)}</span>
                      {tool.badge && <NavItemBadge kind={tool.badge} />}
                    </Link>
                    {user && !ALWAYS_SHOWN_SLUGS.has(tool.slug) && (
                      <PinButton
                        pinned={pinned}
                        reveal='pinned'
                        onToggle={() => {
                          track('pin', tool.slug, {
                            pinned: !pinned,
                            source: 'flyout',
                          });
                          toggleFavorite(tool.slug);
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div
        className={clsxm(
          'flex items-center justify-end border-t px-4 py-2.5',
          nav.hairline,
          nav.soft,
        )}
      >
        <Link
          href='/presets'
          onClick={() => track('flyout', '/presets')}
          className={clsxm(
            'group inline-flex items-center gap-1 rounded text-[13px] font-medium transition-colors hover:text-primary-700 dark:hover:text-primary-300',
            nav.body,
            nav.focus,
          )}
        >
          {t('See all tools')}
          <ArrowRightIcon
            aria-hidden='true'
            className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5'
          />
        </Link>
      </div>
    </div>,
    document.body,
  );
}
